"use client";

import { useEffect, useMemo, useState } from "react";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { Button, Card, CardBody, Divider, Spinner } from "@nextui-org/react";
import { ArrowLeft, Printer } from "lucide-react";
import type { WooCommerceProduct } from "@/types/woocommerce";
import { fetchProductById } from "@/lib/woocommerce";
import { searchParamsToFilters } from "@/lib/filterQuery";

function formatSelectedSpecs(filters: ReturnType<typeof searchParamsToFilters>) {
  const rows: { label: string; value: string }[] = [];

  if (filters.gorivo?.length) rows.push({ label: "Fuel", value: filters.gorivo.join(", ") });
  if (filters.frekvencija?.length) rows.push({ label: "Frequency", value: filters.frekvencija.join(", ") });
  if (filters.faza?.length) rows.push({ label: "Phase", value: filters.faza.join(", ") });
  if (filters.emisija?.length) rows.push({ label: "Emission", value: filters.emisija.join(", ") });

  if (filters.minSnaga !== undefined || filters.maxSnaga !== undefined) {
    const min = filters.minSnaga ?? "—";
    const max = filters.maxSnaga ?? "—";
    rows.push({ label: "Power range (kW)", value: `${min} - ${max}` });
  }

  return rows;
}

export default function ProductDetailPage() {
  const router = useRouter();
  const params = useParams<{ id: string }>();
  const searchParams = useSearchParams();

  const [product, setProduct] = useState<WooCommerceProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filters = useMemo(() => searchParamsToFilters(searchParams), [searchParams]);
  const selectedSpecs = useMemo(() => formatSelectedSpecs(filters), [filters]);

  useEffect(() => {
    const id = Number(params.id);
    if (!Number.isFinite(id)) {
      setError("Invalid product id");
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);
    fetchProductById(id)
      .then((p) => {
        if (!isMounted) return;
        setProduct(p);
        setError(null);
      })
      .catch((e) => {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : "Failed to load product");
        setProduct(null);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [params.id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" color="danger" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-3xl mx-auto">
          <Button variant="flat" startContent={<ArrowLeft className="w-4 h-4" />} onClick={() => router.back()}>
            Back
          </Button>
          <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
            <h1 className="text-xl font-bold text-gray-900">Couldn’t load product</h1>
            <p className="text-gray-600 mt-2">{error ?? "Unknown error"}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between datasheet-print-hide">
          <Button variant="flat" startContent={<ArrowLeft className="w-4 h-4" />} onClick={() => router.back()}>
            Back
          </Button>
          <Button color="primary" startContent={<Printer className="w-4 h-4" />} onClick={() => window.print()}>
            Print data sheet
          </Button>
        </div>

        <div className="datasheet mt-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-bold text-gray-900">{product.name}</h1>
            <div className="text-sm text-gray-600">
              {product.sku ? <span><span className="font-semibold">SKU:</span> {product.sku}</span> : null}
              {product.categories?.length ? (
                <span className="ml-4">
                  <span className="font-semibold">Category:</span> {product.categories.map((c) => c.name).join(", ")}
                </span>
              ) : null}
            </div>
          </div>

          <Divider className="my-5" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border border-gray-200 shadow-none">
              <CardBody>
                <h2 className="text-lg font-bold text-gray-900 mb-3">Selected specification</h2>
                {selectedSpecs.length === 0 ? (
                  <p className="text-sm text-gray-600">No filters were passed to this page.</p>
                ) : (
                  <dl className="space-y-2">
                    {selectedSpecs.map((row) => (
                      <div key={row.label} className="flex items-start justify-between gap-4">
                        <dt className="text-sm text-gray-700 font-semibold">{row.label}</dt>
                        <dd className="text-sm text-gray-900 text-right">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </CardBody>
            </Card>

            <Card className="border border-gray-200 shadow-none">
              <CardBody>
                <h2 className="text-lg font-bold text-gray-900 mb-3">Product specification</h2>
                {product.attributes?.length ? (
                  <dl className="space-y-2">
                    {product.attributes
                      .filter((a) => a.visible)
                      .map((attr) => (
                        <div key={`${attr.id}-${attr.name}`} className="flex items-start justify-between gap-4">
                          <dt className="text-sm text-gray-700 font-semibold">{attr.name}</dt>
                          <dd className="text-sm text-gray-900 text-right">{attr.options.join(", ")}</dd>
                        </div>
                      ))}
                  </dl>
                ) : (
                  <p className="text-sm text-gray-600">No attributes found for this product.</p>
                )}
              </CardBody>
            </Card>
          </div>

          {product.short_description ? (
            <>
              <Divider className="my-5" />
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-2">Notes</h2>
                <div
                  className="prose prose-sm max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: product.short_description }}
                />
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}


